/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BuilderFormSchemaFactory,
  FormRootSchema,
  FormStateSchema,
  PbHousePlanFinderListItem,
} from '@gh/builder-shared';
import {
  collectVisibleValues,
  FormState,
  MaskedTypes,
  toAppFormControl,
  toAppFormGroup,
  whenUsOrAuRegion,
  whenUsRegion,
} from '@gh/common-shared';
import { AutocompleteItem } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { FormMode, getControl, getControlValue, setControlValue } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { get } from 'lodash';
import { JobCustomer, JobQuickDetails } from '../../resources/job.model';
import { ValidatorFn } from '@angular/forms';

@Injectable()
export class JobQuickDetailsFormState extends FormState<JobQuickDetails> {
  constructor(schemaFactory: BuilderFormSchemaFactory,
              private translations: TranslationService) {
    super(schemaFactory);
  }

  get priceBook(): Maybe<AutocompleteItem> {
    return getControlValue(this.group, 'priceBook');
  }

  get housePlan(): Maybe<PbHousePlanFinderListItem> {
    return getControlValue(this.group, 'housePlan');
  }

  get contact(): Maybe<JobCustomer> {
    return getControlValue(this.group, 'contact');
  }

  get isQqShow(): boolean {
    return getControlValue(this.group, 'isQqShow');
  }

  get submitValue(): JobQuickDetails {
    return {
      ...collectVisibleValues(toAppFormGroup(this.group)),
      salesFolderName: this.translations.get('lbl_quick_job'),
      quotationDescription: this.translations.get('lbl_quick_job'),
    };
  }

  build(): void {
    this.initSchema(<any>{
      isQqShow: false,
    });
  }

  setHousePlan(housePlan: PbHousePlanFinderListItem): void {
    setControlValue(this.group, 'housePlan', housePlan);
    setControlValue(this.group, 'housePlanName', housePlan.name);
  }

  clearHousePlan(): void {
    setControlValue(this.group, 'housePlan', void 0);
    setControlValue(this.group, 'housePlanName', void 0);
  }

  setContact(contact?: JobCustomer): void {
    const assignedContact = this.contact;
    setControlValue(this.group, 'contact', contact);

    if (get(assignedContact, 'contactId') !== get(contact, 'contactId')) {
      this.updateContact(contact);
    }
  }

  updateContact(contact?: JobCustomer): void {
    const hasSalesPerson = isNotNil(contact && contact.salespersonId);
    setControlValue(this.group, 'salesperson', hasSalesPerson ? {
      userId: contact!.salespersonId, // tslint:disable-line:no-non-null-assertion
      userName: contact!.salespersonName, // tslint:disable-line:no-non-null-assertion
    } : void 0);
    setControlValue(this.group, 'owner1', contact && contact.name);

    toAppFormControl(getControl(this.group, 'salesperson')).updateSchema({
      enabled: isNotNil(contact) && !hasSalesPerson,
    });
  }

  updatePriceBook(pb?: AutocompleteItem): void {
    setControlValue(this.group, 'housePlan', void 0);
    toAppFormControl(getControl(this.group, 'housePlan')).updateSchema({
      enabled: isNotNil(pb),
    });
  }

  googleMapUrlValidator(): ValidatorFn {
    return (control): { [key: string]: any } | null => {
      const value = control.value;
      const maxLength = 500;
      if (!value) {
        return null;
      }
      if (value && value.length > maxLength) {
        return { pattern: { messageId: 'msg_field_maxlength', requiredLength: maxLength } };
      }
      const patternGoogleUrl = /^https:\/\/www\.google\.com(.[a-z]{2})?\/maps\//;
      const invalid = !patternGoogleUrl.test(value);

      // tslint:disable-next-line:no-null-keyword
      return invalid ? { pattern: { messageId: 'msg_invalid_url' } } : null;
    };
  }

  protected buildSchema(_: JobQuickDetails, { root }: FormStateSchema): FormRootSchema {
    return root({
      mode: FormMode.CreateMode,
      children: {
        contact: { required: true },
        salesperson: { required: true, enabled: false },
        owner1: { required: true },
        owner2: void 0,
        owner3: void 0,
        siteName: { required: true },
        lotNumber: void 0,
        suburb: void 0,
        streetNumber: void 0,
        street: void 0,
        postCode: { visible: whenUsOrAuRegion(), type: MaskedTypes.Zip },
        mapReference: {
          validators: [
            this.googleMapUrlValidator(),
          ],
        },
        reference: { visible: whenUsRegion() },
        priceBook: { required: true },
        isQqShow: void 0,
        housePlan: { required: true, enabled: false },
        housePlanName: void 0,
        baseDesign: { required: true },
        jobMemo: void 0,
      },
    });
  }
}
